import i18n from "i18next";

import CurrentWorkspace from "./Workspace";

import { NvencRingVideoSender } from "./webcodecs/NvencRingVideoSender";

import { AudioHardwareEncoder } from "./webcodecs/AudioEncoder";

import {
  WebTransportManager,
  type BroadcastStats,
  type SelfHostConfig,
  type WebTransportSelfHostedConfig,
} from "./WebTransport";

import type { StreamHealthInput } from "../services/cloudStreamService";

import { DEFAULT_ENCODERS_CONFIG, getEncodersConfig } from "./webcodecs/config";

import type { ProviderEncoding } from "./streamQualityPresets";

import { parseLocalBroadcastStatus } from "./localBroadcastStatus";

import {
  BroadcastStatsTracker,
  parseLocalBroadcastStats,
  type LocalBroadcastSnapshot,
} from "./localBroadcastStats";

import type { BroadcastConnectionStats } from "../broadcast/sceneSync";

/** Canales IPC main → OSR del broadcast LOCAL: status de kero (fallos/reconexión) y stats 1/s. */
const LOCAL_STATUS_CHANNEL = "osr-broadcast-status";

const LOCAL_STATS_CHANNEL = "osr-broadcast-stats";

export interface IProvider {
  endpoint: string;

  enabled: boolean;

  key: string;

  name: string;

  /** Override de calidad por destino (kero re-encodea esa rendition en cloud/selfhost;
   * en LOCAL kero lo ignora). Ausente → el destino usa el encoding maestro. Bitrate en bps. */

  encoding?: ProviderEncoding;

  /** Ruteo: "local" (Kero fan-out directo, gratis, ∞) o "cloud" (Cloudflare Live Output,
   * pago, limitado por plan). Ausente → "local". El broadcast cloud crea Live Outputs para
   * los "cloud" y agrega UN provider extra (el ingest de Cloudflare) para que Kero pushee ahí. */

  routing?: "cloud" | "local";
}

export enum StreamingState {
  IDLE = "IDLE",

  STARTING = "STARTING",

  STARTED = "STARTED",

  STOPPING = "STOPPING",

  STOPPED = "STOPPED",
}

interface RecordingConfig {
  enabled: boolean;

  basePath?: string;
}

interface StreamConfig {
  width: number;

  height: number;

  frameRate: number;

  videoBitrate: number;

  audioBitrate: number;

  recording: RecordingConfig;
}

interface ReconnectStatus {
  source: string;

  attempt: number;

  maxAttempts: number;

  status: "reconnecting" | "reconnected" | "failed";
}

interface ParsedServerError {
  code?: string;

  source?: string;

  sink?: string;

  message?: string;

  remaining_destinations?: number;

  remainingDestinations?: number;

  failed_destinations?: number;

  failedDestinations?: number;

  total_destinations?: number;

  totalDestinations?: number;
}

/** Salud del encoder acumulada durante un broadcast (Fase 5.1). Ver `broadcastHealth`. */
interface BroadcastHealthAccumulator {
  maxDropped: number;

  avgBitrateKbps: number;

  renditions: number;

  rttMs: number;

  samples: number;
}

class Streaming {
  private listeners: { [key: string]: Array<(data: unknown) => void> } = {};

  private State: StreamingState = StreamingState.IDLE;

  private Providers: IProvider[] = [];

  private SelfhostConfig: SelfHostConfig | null = null;

  private enabledRtmpDestinations = 0;

  // Etapa 3: true mientras el broadcast actual es LOCAL por SHM (sin WebTransport) → el screenshot y el

  // stop se rutean por IPC (control mailbox) en vez de WebTransport.

  private localShmActive = false;

  /** true mientras estamos suscriptos a los canales de status/stats de kero (broadcast local). */
  private localStatusBound = false;

  /** Deriva fps/kbps de los contadores acumulados que el main empuja 1/s en el broadcast LOCAL. */
  private localStatsTracker = new BroadcastStatsTracker();

  /** Última foto derivada de las stats locales; null hasta la primera muestra (o sin local). */
  private localStatsSnapshot: LocalBroadcastSnapshot | null = null;

  private failedRtmpSinks = new Set<string>();

  // Salud del encoder acumulada durante el broadcast (Fase 5.1): peak de frames caídos +

  // último bitrate/renditions. En cloud/selfhost la reporta Kero (datagram 0x08); en LOCAL sale

  // de las stats del encoder del OSR. Se resetea al arrancar.

  private broadcastHealth: BroadcastHealthAccumulator | null = null;

  private transportManager: WebTransportManager;

  private videoHandler: NvencRingVideoSender;

  private audioHandler: AudioHardwareEncoder;

  private systemAudioVolume = 1;

  private systemAudioMuted = true;

  private config: StreamConfig = {
    width: 1920,

    height: 1080,

    frameRate: DEFAULT_ENCODERS_CONFIG.framerate,

    videoBitrate: DEFAULT_ENCODERS_CONFIG.bitrate,

    audioBitrate: DEFAULT_ENCODERS_CONFIG.audioBitrate,

    recording: {
      enabled: true,

      basePath: undefined,
    },
  };

  constructor() {
    this.transportManager = new WebTransportManager();

    this.videoHandler = new NvencRingVideoSender();

    this.audioHandler = new AudioHardwareEncoder();

    this.videoHandler.setOnStop(() => this.stop());

    this.transportManager.setOnClosed(() => this.stop());

    // Salud del encoder (Fase 5.1): Kero emite stats periódicas (datagram 0x08) mientras

    // hay WebTransport (cloud/selfhost). Acumulamos peak de dropped + último bitrate/renditions.

    this.transportManager.setOnBroadcastStats((stats: BroadcastStats) => {
      const h = this.ensureBroadcastHealth();

      if (typeof stats.droppedFrames === "number") {
        h.maxDropped = Math.max(h.maxDropped, stats.droppedFrames);
      }

      if (
        typeof stats.avgBitrateKbps === "number" &&
        stats.avgBitrateKbps > 0
      ) {
        h.avgBitrateKbps = stats.avgBitrateKbps;
      }

      if (typeof stats.renditions === "number" && stats.renditions > 0) {
        h.renditions = stats.renditions;
      }

      if (typeof stats.rttMs === "number" && stats.rttMs > 0) {
        h.rttMs = stats.rttMs;
      }

      h.samples += 1;
    });
  }

  /** Acumulador de salud del broadcast en curso; lo crea en la primera muestra. */
  private ensureBroadcastHealth(): BroadcastHealthAccumulator {
    if (!this.broadcastHealth) {
      this.broadcastHealth = {
        maxDropped: 0,

        avgBitrateKbps: 0,

        renditions: 0,

        rttMs: 50,

        samples: 0,
      };
    }

    return this.broadcastHealth;
  }

  /**
   * Devuelve (y limpia) el resumen de salud del encoder acumulado en este broadcast, o null
   * si no hubo muestras. Lo consume BroadcastManager.stop() para reportarlo al backend.
   */

  consumeBroadcastHealth(): StreamHealthInput | null {
    const h = this.broadcastHealth;

    this.broadcastHealth = null;

    if (!h || h.samples === 0) return null;

    const out: StreamHealthInput = {};

    if (h.avgBitrateKbps > 0) out.avgBitrateKbps = h.avgBitrateKbps;

    if (h.maxDropped > 0) out.droppedFrames = h.maxDropped;

    if (h.renditions > 0) out.renditions = h.renditions;

    return Object.keys(out).length > 0 ? out : null;
  }

  /**
   * Foto actual de la salud del broadcast para la UI del control (no limpia nada). En LOCAL
   * sale del tracker de stats (encoder del OSR + salidas RTMP de kero); con WebTransport
   * (cloud/selfhost) sale del acumulador que alimenta el datagram 0x08. Vacía hasta la
   * primera muestra.
   */

  getBroadcastStats(): BroadcastConnectionStats {
    const local = this.localStatsSnapshot;

    if (local) {
      return { encoder: local.encoder, outputs: local.outputs, transport: null };
    }

    const h = this.broadcastHealth;

    if (!h) return { encoder: null, outputs: [], transport: null };

    return {
      encoder: null,

      outputs: [],

      transport: {
        avgBitrateKbps: h.avgBitrateKbps,

        droppedFrames: h.maxDropped,

        rttMs: h.rttMs,
      },
    };
  }

  /**
   * Check if WebCodecs is supported in this environment
   */

  static isSupported(): boolean {
    return (
      typeof VideoEncoder !== "undefined" &&
      typeof AudioEncoder !== "undefined" &&
      typeof VideoEncoder.isConfigSupported === "function"
    );
  }

  setConfig(config: Partial<StreamConfig>) {
    this.config = { ...this.config, ...config };
  }

  getSystemAudio() {
    return {
      muted: this.systemAudioMuted,

      volume: this.systemAudioVolume,
    };
  }

  async setSystemAudioMuted(muted: boolean) {
    this.systemAudioMuted = muted;

    this.emit("audio-control-changed", this.getSystemAudio());
  }

  async setSystemAudioVolume(volume: number) {
    const clampedVolume = Math.max(0, Math.min(1, volume));

    this.systemAudioVolume = clampedVolume;

    this.emit("audio-control-changed", this.getSystemAudio());
  }

  isStreaming() {
    return this.State === StreamingState.STARTED;
  }

  setProviders(providers: IProvider[]) {
    this.Providers = providers;
  }

  setSelfhostConfig(config: SelfHostConfig | null) {
    this.SelfhostConfig = config;
  }

  /**
   * Set callback for reconnection status updates from Kero
   */

  setOnReconnectStatus(callback: (status: ReconnectStatus) => void) {
    this.transportManager.setOnReconnectStatus(callback);
  }

  async broadcast(stream: MediaStream) {
    if (
      this.State === StreamingState.STARTED ||
      this.State === StreamingState.STARTING
    ) {
      return;
    }

    this.State = StreamingState.STARTING;

    if (!Streaming.isSupported()) {
      throw new Error("WebCodecs not supported in this environment");
    }

    // [OSR/NVENC] El video lo encodea NVENC nativo (hardware por definición) en la ventana OSR,

    // no WebCodecs → el chequeo de VideoHardwareEncoder ya no aplica (el símbolo no existe acá).

    // El gate real es la construcción del NvencEncoder nativo: si no hay GPU NVIDIA, falla ahí.

    // Tipo de workspace, carpeta de grabación y config de encoders son lecturas independientes
    // (cada una un round-trip a Dexie): en paralelo, no en serie.
    const [userType, recordingBasePath, encodersConfig] = await Promise.all([
      CurrentWorkspace.getType(),
      CurrentWorkspace.getRecordingPath(),
      getEncodersConfig(),
    ]);

    // Preferencia "Grabar esta transmisión (cloud)" seteada en la pantalla de

    // Transmisión (control window). El control y el OSR comparten origin →

    // mismo localStorage. true → el streaming-server graba el VOD del live.

    const cloudRecord =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("@Eriol/recording/cloud") === "true";

    try {
      const isRecordingOnly = userType === "local_recording";

      // Audio encode-once: el cliente→kero es siempre LOCAL (loopback), así que mandamos PCM crudo en vez

      // de encodear (Opus/AAC) y que kero decodifique. kero (appsrc audio/x-raw) mezcla + encodea AAC UNA

      // vez. Menos CPU del renderer (que corre Pixi) + sin transcoding lossy. El worker ve audioCodec=pcm

      // y manda PCM; kero arma el receiver raw. (No afecta el output: kero igual saca AAC a RTMP/SRT.)

      encodersConfig.audioCodec = "pcm";

      // Transporte LOCAL por SHM: ringName/ringCap viajan en la URL del OSR. kero corre local

      // (en loopback, 127.0.0.1). En local (no selfhost) NO se abre WebTransport: el config va por el

      // control mailbox (IPC→MAIN→kero), y video/audio por SHM. nativePath: el worker de audio escribe el ring.

      const sp = new URLSearchParams(window.location.search);

      const ringName = sp.get("ring");

      const ringCapacity = parseInt(sp.get("ringCap") || "8388608", 10);

      const nativePath = (
        window.electronAPI as unknown as {
          getNativeAddonPath?: () => string | null;
        }
      )

        ?.getNativeAddonPath?.();

      const useLocalShm = !this.SelfhostConfig && !!ringName;

      const audioRingName =
        useLocalShm && nativePath && ringName ? `${ringName}-audio` : null;

      this.localShmActive = useLocalShm;

      if (useLocalShm) {
        this.bindLocalStatusChannel();
      }

      const providersForSession = isRecordingOnly
        ? []
        : this.Providers.filter((provider) => provider.enabled);

      this.enabledRtmpDestinations = providersForSession.length;

      this.failedRtmpSinks.clear();

      // Reset del acumulador de salud y del tracker de stats locales para este broadcast.

      this.broadcastHealth = null;

      this.localStatsTracker.reset();

      this.localStatsSnapshot = null;

      // En LOCAL no se abre WebTransport (config por mailbox, A/V por SHM). Selfhost/remoto sí.

      if (!useLocalShm) {
        await this.transportManager.connect();
      }

      // Register server error handler — terminal errors stop the stream

      this.transportManager.setOnServerError((message) => {
        console.error("Server error (terminal):", message);

        // Parse error message for better handling

        let errorInfo = null;

        try {
          errorInfo = JSON.parse(message) as ParsedServerError;
        } catch {
          // Not JSON, use as-is
        }

        // Check if it's a self-host error from Kero

        if (errorInfo?.code?.startsWith("SELFHOST_")) {
          const translationKey = `streaming:SELFHOST_ERRORS.${errorInfo.code}`;

          let translatedMessage = i18n.t(translationKey);

          if (translatedMessage === translationKey) {
            translatedMessage = cleanErrorMessage(errorInfo.message || message);
          }

          this.emit("server-error", { message: translatedMessage });

          this.stop();

          return;
        }

        // Check for RTMP errors

        if (
          errorInfo?.code?.startsWith("RTMP_") ||
          errorInfo?.code === "rtmpConnectionFailed" ||
          message.toLowerCase().includes("rtmp") ||
          message.toLowerCase().includes("could not open resource")
        ) {
          const source = errorInfo?.source || extractProviderName(message);

          const sinkIdentifier =
            errorInfo?.sink || source || extractProviderName(message);

          if (sinkIdentifier) {
            this.failedRtmpSinks.add(sinkIdentifier);
          }

          const translationKey = `streaming:STREAMING_ERRORS.${
            errorInfo?.code || "rtmpConnectionFailed"
          }`;

          let errorDescription = i18n.t(translationKey);

          if (errorDescription === translationKey) {
            errorDescription = i18n.t(
              "streaming:STREAMING_ERRORS.rtmpConnectionFailed",
            );
          }

          if (
            errorDescription ===
            "streaming:STREAMING_ERRORS.rtmpConnectionFailed"
          ) {
            errorDescription = cleanErrorMessage(errorInfo?.message || message);
          }

          const totalDestinations =
            parseOptionalNumber(
              errorInfo?.total_destinations ?? errorInfo?.totalDestinations,
            ) ?? this.enabledRtmpDestinations;

          const failedDestinations =
            parseOptionalNumber(
              errorInfo?.failed_destinations ?? errorInfo?.failedDestinations,
            ) ?? this.failedRtmpSinks.size;

          const remainingDestinations =
            parseOptionalNumber(
              errorInfo?.remaining_destinations ??
                errorInfo?.remainingDestinations,
            ) ?? Math.max(0, totalDestinations - failedDestinations);

          const shouldStop = remainingDestinations <= 0;

          this.emit("rtmp-error", {
            source: sanitizeSourceName(source),

            message: errorDescription,

            originalMessage: message,

            remainingDestinations,

            failedDestinations,

            totalDestinations,

            shouldStop,
          });

          return;
        }

        this.emit("server-error", { message: cleanErrorMessage(message) });

        this.stop();
      });

      // Register connection-lost handler

      this.transportManager.setOnConnectionLost((reason) => {
        console.error("Connection lost:", reason);

        this.emit("connection-lost", { message: reason });
      });

      // Register reconnect status handler

      this.transportManager.setOnReconnectStatus((status: ReconnectStatus) => {
        this.emit("reconnect-status", status);
      });

      let configReady: boolean;

      // Detect if self-hosted or cloud mode

      if (this.SelfhostConfig) {
        // Self-hosted mode: SRT streaming via API v1

        const selfhostConfig: WebTransportSelfHostedConfig = {
          selfhost_config: this.SelfhostConfig,

          providers: providersForSession,

          width: this.config.width,

          height: this.config.height,

          encoders: encodersConfig,

          recording: {
            enabled: isRecordingOnly ? true : this.config.recording.enabled,

            basePath: recordingBasePath ?? this.config.recording.basePath,

            record: cloudRecord,
          },
        };

        configReady =
          await this.transportManager.sendSelfhostedConfig(selfhostConfig);
      } else if (useLocalShm) {
        // LOCAL sin WebTransport: el config va por el control mailbox (IPC → MAIN → watcher de kero).

        // No hay handshake {ready}: kero crea el pipeline async y los rings bufferean. video+audio por SHM.

        const configData = {
          type: "config",

          width: this.config.width,

          height: this.config.height,

          encoders: encodersConfig,

          recording: {
            enabled: isRecordingOnly ? true : this.config.recording.enabled,

            basePath: recordingBasePath ?? this.config.recording.basePath,

            record: cloudRecord,
          },

          providers: providersForSession.map((p) => ({
            name: p.name,

            endpoint: p.endpoint,

            key: p.key,

            // En LOCAL kero ignora el override, pero lo mandamos igual para mantener el

            // shape uniforme con cloud/selfhost (omitido cuando no hay override).

            ...(p.encoding ? { encoding: p.encoding } : {}),
          })),

          source: "local_shm",

          ringName,

          ringCap: ringCapacity,

          ...(audioRingName
            ? { audioRingName, audioRingCap: ringCapacity }
            : {}),
        };

        (
          window as unknown as {
            electronAPI?: { send?: (c: string, ...a: unknown[]) => void };
          }
        ).electronAPI?.send?.(
          "osr-broadcast-config",

          JSON.stringify(configData),
        );

        configReady = true;
      } else {
        // Cloud/remoto: GStreamer pipeline vía WebTransport (sin SHM local).

        const pipelineReady = await this.transportManager.sendConfig({
          width: this.config.width,

          height: this.config.height,

          encoders: encodersConfig,

          recording: {
            enabled: isRecordingOnly ? true : this.config.recording.enabled,

            basePath: recordingBasePath ?? this.config.recording.basePath,

            record: cloudRecord,
          },

          providers: providersForSession,
        });

        configReady = pipelineReady;
      }

      if (!configReady) {
        throw new Error(
          "Pipeline configuration was not confirmed ready by the server",
        );
      }

      // En LOCAL no hay WebTransport (config por mailbox, A/V por SHM). En selfhost/remoto sí.

      const transport = useLocalShm
        ? null
        : this.transportManager.getTransport();

      if (!useLocalShm && !transport) {
        throw new Error("WebTransport not connected");
      }

      void isRecordingOnly;

      // Video: el H.264 ya viene del addon NVENC vía el ring SHM. En LOCAL (useLocalShm) kero LEE el

      // ring directo → NO abrimos el uni-stream de video acá (evita el doble-lector del ring + el

      // cifrado QUIC sobre loopback). En selfhost/otros, el sender del renderer reenvía el ring por WT.

      if (useLocalShm) {
        console.log(
          "📺 Video por SHM local: kero lee el ring directo (sin uni-stream de video por WebTransport)",
        );
      } else {
        if (!ringName || !nativePath) {
          throw new Error(
            "Broadcast NVENC-OSR: falta ring/nativePath (¿no se inició vía startOsrBroadcast?)",
          );
        }

        await this.videoHandler.init(
          transport!,

          ringName,

          ringCapacity,

          nativePath,
        );

        await this.videoHandler.start();
      }

      await this.audioHandler.init(
        encodersConfig,

        transport,

        audioRingName && nativePath
          ? { ringName: audioRingName, ringCap: ringCapacity, nativePath }
          : null,
      );

      const audioTrack = stream.getAudioTracks()[0];

      if (audioTrack) {
        this.audioHandler.start(audioTrack);
      } else {
        console.warn("No audio track found");
      }

      this.State = StreamingState.STARTED;

      this.emit("started");
    } catch (e) {
      console.error("Failed to start streaming:", e);

      this.transportManager.close();

      this.State = StreamingState.STOPPED;

      this.emit("error", e);

      throw e;
    }
  }

  getState() {
    return this.State;
  }

  async updateScreenshot(path: string) {
    if (this.localShmActive) {
      // LOCAL: el screenshot path va por el control mailbox (IPC → MAIN), no por WebTransport.

      try {
        (
          window as unknown as {
            electronAPI?: { send?: (c: string, ...a: unknown[]) => void };
          }
        ).electronAPI?.send?.("osr-broadcast-screenshot", path);
      } catch {
        /* editor / sin electronAPI */
      }

      return;
    }

    await this.transportManager.updateScreenshotPath(path);
  }

  /**
   * Canales de vuelta kero -> extensión -> renderer para el broadcast LOCAL. En local no se abre
   * WebTransport, así que ninguno de los hooks de salud del transporte dispara: sin esto un fallo
   * de RTMP quedaba sólo en el log de kero y la app seguía mostrando "emitiendo" indefinidamente.
   * Por el mismo camino llegan las stats (1/s) del encoder y de las salidas RTMP.
   */
  private bindLocalStatusChannel() {
    if (this.localStatusBound) return;

    const api = (
      window as unknown as {
        electronAPI?: { on?: (c: string, cb: (d?: unknown) => void) => void };
      }
    ).electronAPI;

    if (!api?.on) return;

    api.on(LOCAL_STATUS_CHANNEL, (payload?: unknown) => {
      this.handleLocalStatus(payload);
    });

    api.on(LOCAL_STATS_CHANNEL, (payload?: unknown) => {
      this.handleLocalStats(payload);
    });

    this.localStatusBound = true;
  }

  private unbindLocalStatusChannel() {
    if (!this.localStatusBound) return;

    const api = (
      window as unknown as {
        electronAPI?: { removeListener?: (c: string) => void };
      }
    ).electronAPI;

    for (const channel of [LOCAL_STATUS_CHANNEL, LOCAL_STATS_CHANNEL]) {
      try {
        api?.removeListener?.(channel);
      } catch {
        // el canal ya no existe: nada que limpiar
      }
    }

    this.localStatusBound = false;
  }

  /**
   * Stats acumuladas (encoder del OSR + salidas RTMP de kero) que el main empuja 1/s en LOCAL.
   * El tracker deriva fps/kbps; la foto la lee `getBroadcastStats()` (→ title bar del control) y
   * alimenta el mismo acumulador de salud que en cloud/selfhost llena el datagram 0x08, para que
   * `consumeBroadcastHealth()` también reporte al backend en LOCAL.
   */
  private handleLocalStats(payload?: unknown) {
    const sample = parseLocalBroadcastStats(payload);

    if (!sample) return;

    const snapshot = this.localStatsTracker.push(sample);

    this.localStatsSnapshot = snapshot;

    if (!snapshot.encoder) return;

    const h = this.ensureBroadcastHealth();

    h.maxDropped = Math.max(h.maxDropped, snapshot.encoder.droppedFrames);

    if (snapshot.encoder.bitrateKbps > 0) {
      h.avgBitrateKbps = snapshot.encoder.bitrateKbps;
    }

    h.samples += 1;
  }

  private handleLocalStatus(payload?: unknown) {
    const event = parseLocalBroadcastStatus(payload);

    if (!event) return;

    const source = sanitizeSourceName(event.source);

    if (event.kind === "reconnect") {
      this.emit("reconnect-status", {
        source,
        attempt: event.attempt,
        maxAttempts: event.maxAttempts,
        status: event.status,
      });

      return;
    }

    // kero se rindió: la emisión está muerta. shouldStop corta de verdad (el Dashboard llama
    // radio.stop()), que es lo que faltaba para que el usuario no siga creyendo que está al aire.
    this.emit("rtmp-error", {
      source,
      message: event.message,
      originalMessage: typeof payload === "string" ? payload : "",
      remainingDestinations: 0,
      failedDestinations: this.enabledRtmpDestinations,
      totalDestinations: this.enabledRtmpDestinations,
      shouldStop: true,
    });
  }

  async stop() {
    if (
      this.State === StreamingState.STOPPED ||
      this.State === StreamingState.STOPPING
    )
      return;

    this.State = StreamingState.STOPPING;

    console.log("🛑 Stopping Streaming...");

    await this.videoHandler.stop();

    await this.audioHandler.stop();

    this.transportManager.close();

    this.unbindLocalStatusChannel();

    this.failedRtmpSinks.clear();

    this.enabledRtmpDestinations = 0;

    this.State = StreamingState.STOPPED;

    this.emit("stopped");

    console.log("🛑 Streaming stopped");
  }

  on(type: string, callback: (data: unknown) => void) {
    if (!this.listeners[type]) {
      this.listeners[type] = [];
    }

    this.listeners[type].push(callback);
  }

  off(type: string, callback: (data: unknown) => void) {
    const callbacks = this.listeners[type];

    if (callbacks) {
      const index = callbacks.indexOf(callback);

      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(type: string, data?: unknown) {
    const callbacks = this.listeners[type];

    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  async destroy() {
    await this.stop();

    this.Providers = [];

    this.listeners = {};
  }
}

/**
 * Clean up error messages for user display
 */

function cleanErrorMessage(errorMsg: string): string {
  const patterns: { [key: string]: string } = {
    "could not open resource for writing": i18n.t(
      "streaming:STREAMING_ERRORS.rtmpConnectionFailed",
    ),

    "could not connect to rtmp stream": i18n.t(
      "streaming:STREAMING_ERRORS.rtmpConnectionFailed",
    ),

    "connection refused": i18n.t(
      "streaming:SELFHOST_ERRORS.CONNECTION_REFUSED",
    ),

    "connection timed out": i18n.t("streaming:SELFHOST_ERRORS.TIMEOUT"),

    "dns error": i18n.t("streaming:SELFHOST_ERRORS.DNS_ERROR"),

    "host desconocido": i18n.t("streaming:SELFHOST_ERRORS.DNS_ERROR"),
  };

  const lowerMsg = errorMsg.toLowerCase();

  for (const [pattern, translation] of Object.entries(patterns)) {
    if (lowerMsg.includes(pattern.toLowerCase())) {
      return translation;
    }
  }

  return errorMsg;
}

/**
 * Extract provider name from error message
 */

function extractProviderName(message: string): string {
  // Try to extract from common patterns like "rtmpsink for rtmp://.../key"

  const match = message.match(/rtmp:\/\/[^/]+\/([^\s/]+)/);

  if (match) {
    return match[1];
  }

  // Try to find any URL-like pattern

  const urlMatch = message.match(/rtmp[^\s]*/);

  if (urlMatch) {
    return urlMatch[0];
  }

  return "Unknown";
}

/**
 * Strip internal GStreamer sink identifiers (e.g. "rtmpsink_0", "rtmpsink_1")
 * so they never leak into user-facing notifications.
 */

function sanitizeSourceName(source: string): string {
  if (/^rtmpsink[_\d]*$/i.test(source) || /^rtmp_queue[_\d]*$/i.test(source)) {
    return "";
  }

  return source;
}

function parseOptionalNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }

  if (typeof value === "string") {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.floor(parsed));
    }
  }

  return null;
}

export default Streaming;
